import React from 'react';
import Tooltip from '../Tooltip';
import './RevenueMetricsCards.css';

const RevenueMetricsCards = ({ data, timeRange, selectedMetric, onMetricSelect, selectedSite, user }) => {
  const isDemoUser = user?.email === 'demo@example.com';
  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) {
      return '$0.00';
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  const formatChange = (change) => {
    if (change === null || change === undefined) {
      return null;
    }
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const formatTimeRangeDisplay = () => {
    if (typeof timeRange === 'object' && timeRange?.custom) {
      const startDate = new Date(timeRange.startDate);
      const endDate = new Date(timeRange.endDate);
      const diffTime = Math.abs(endDate - startDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return days;
    }
    if (typeof timeRange === 'number') {
      return timeRange;
    }
    return 'N';
  };

  const getChangeIcon = (change) => {
    if (change > 0) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 3l5 5H3z" />
        </svg>
      );
    }
    if (change < 0) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 13l-5-5h10z" />
        </svg>
      );
    }
    return null;
  };

  // Safely extract numeric values, ensuring they're numbers
  const safeValue = (obj, key) => {
    const val = obj?.[key]?.value;
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };
  
  const safeChange = (obj, key) => {
    const val = obj?.[key]?.change;
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  const metrics = [
    {
      key: 'revenue',
      label: 'Revenue',
      value: safeValue(data, 'revenue'),
      change: safeChange(data, 'revenue'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total AdSense revenue generated from your website. This is the total earnings from ads displayed on your pages.",
      isCurrency: true
    },
    {
      key: 'rpm',
      label: 'RPM',
      value: safeValue(data, 'rpm'),
      change: safeChange(data, 'rpm'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Revenue Per Mille (per 1,000 pageviews). Higher RPM indicates better ad performance and monetization.",
      isCurrency: true
    },
    {
      key: 'cpm',
      label: 'CPM',
      value: safeValue(data, 'cpm'),
      change: safeChange(data, 'cpm'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Cost Per Mille (per 1,000 ad impressions). CPM measures the revenue generated per thousand ad impressions.",
      isCurrency: true
    },
    {
      key: 'rpu',
      label: 'RPU',
      value: safeValue(data, 'rpu'),
      change: safeChange(data, 'rpu'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Revenue Per User. RPU measures the average revenue generated per user, helping you understand user monetization value.",
      isCurrency: true
    },
    {
      key: 'pageviews',
      label: 'Pageviews',
      value: safeValue(data, 'pageviews'),
      change: safeChange(data, 'pageviews'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total number of pages viewed. If someone visits 3 pages, that counts as 3 pageviews.",
      formatNumber: (num) => {
        if (num >= 1000000) {
          return `${(num / 1000000).toFixed(1)}M`;
        }
        if (num >= 1000) {
          return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toLocaleString();
      }
    },
    {
      key: 'adImpressions',
      label: 'Ad Impressions',
      value: safeValue(data, 'adImpressions'),
      change: safeChange(data, 'adImpressions'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total number of ad impressions served. An impression is counted each time an ad is displayed.",
      formatNumber: (num) => {
        if (num >= 1000000) {
          return `${(num / 1000000).toFixed(1)}M`;
        }
        if (num >= 1000) {
          return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toLocaleString();
      }
    },
    {
      key: 'viewability',
      label: 'Viewability',
      value: safeValue(data, 'viewability'),
      change: safeChange(data, 'viewability'),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ),
      positive: true,
      clickable: false,
      tooltip: "Average viewability rate (percentage of ad impressions that were viewable). Higher viewability indicates better ad visibility.",
      formatNumber: (num) => {
        return `${num.toFixed(1)}%`;
      }
    }
  ];

  return (
    <div className="revenue-metrics-cards">
      {metrics.map((metric, index) => {
        const isSelected = selectedMetric === metric.key;
        const isClickable = metric.clickable;
        const needsAdSense = !selectedSite?.adsenseAccountId && !selectedSite?.adsenseAccountName && !isDemoUser;
        
        return (
          <div 
            key={index} 
            className={`revenue-metric-card ${isClickable ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
            onClick={isClickable ? () => onMetricSelect(metric.key) : undefined}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
          >
            <div className="metric-header">
              <div className="metric-icon">{metric.icon}</div>
              <div className="metric-label-container">
                <span className="metric-label">{metric.label}</span>
                {metric.tooltip && (
                  <Tooltip content={metric.tooltip} position="top" metricId={metric.key}>
                    <div className="tooltip-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className="metric-value">
              {metric.isCurrency 
                ? formatCurrency(metric.value)
                : metric.formatNumber 
                  ? metric.formatNumber(metric.value)
                  : metric.value.toLocaleString()}
            </div>
            {metric.change !== null && metric.change !== undefined && (
              <div className={`metric-change ${metric.change > 0 ? 'positive' : metric.change < 0 ? 'negative' : 'neutral'}`}>
                {getChangeIcon(metric.change)}
                <span>{formatChange(metric.change)}</span>
                <span className="vs-previous">vs previous {formatTimeRangeDisplay()} days</span>
              </div>
            )}
            {needsAdSense && (metric.key === 'revenue' || metric.key === 'rpm' || metric.key === 'cpm') && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
                color: '#92400e',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Connect AdSense</div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.9 }}>
                  Connect your AdSense account to see revenue data
                </div>
              </div>
            )}
            {isClickable && (
              <div className="chart-indicator">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18M7 12l4-4 4 4 4-4" />
                </svg>
                <span>Click to chart</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RevenueMetricsCards;

